import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { getCachedCommercialMeteringPointDocumentsAndActsSelector } from '@/features/commercialMeteringPoint/commercialMeteringPointsApi';
import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { findById, infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { FiltersGrid, Pagination } from '../types';
import { TechnicalCondition } from './types';

export const technicalConditionsApi = createApi({
  reducerPath: 'technicalConditionsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/technical-conditions-acts`,
  }),
  tagTypes: ['TechnicalCondition', 'TechnicalConditions'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getTechnicalConditionsI: builder.infiniteQuery<
      TechnicalCondition[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['TechnicalCondition', 'TechnicalConditions'],
    }),
    getTechnicalConditionById: builder.query<TechnicalCondition, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['TechnicalCondition', 'TechnicalConditions'],
    }),
    createTechnicalCondition: builder.mutation<TechnicalCondition, FormData>({
      query: data => ({
        url: '/',
        method: 'POST',
        data: data,
      }),
      invalidatesTags: result => [
        'TechnicalCondition',
        { type: 'TechnicalCondition', id: result?.id },
      ],
    }),
    updateTechnicalConditionById: builder.mutation<
      TechnicalCondition,
      FormData
    >({
      query: data => ({
        url: `/${data.get('id')}`,
        method: 'PUT',
        data: data,
      }),
      invalidatesTags: result => [
        'TechnicalCondition',
        { type: 'TechnicalCondition', id: result?.id },
      ],
    }),
  }),
});

export const {
  useCreateTechnicalConditionMutation,
  useGetTechnicalConditionByIdQuery,
  useUpdateTechnicalConditionByIdMutation,
} = technicalConditionsApi;

export const useGetCachedTechnicalConditionByIdSelector = (id: string) => {
  const selector = useSelector(
    getCachedCommercialMeteringPointDocumentsAndActsSelector,
  );

  // @ts-ignore
  return findById(id, { selector });
};

export const useGetTechnicalConditionInfiniteQuery = normalizeInfiniteQueryData(
  technicalConditionsApi.useGetTechnicalConditionsIInfiniteQuery,
);
